import { ReceiptTokenFeeInfo, YEAR } from "@mezo/sdk"
import { DateTime } from "luxon"

export type DepositFeeData = {
  depositId: number | null
  feeOwed: bigint
  lastFeeIntegral: bigint
}

/**
 * Calculates max mint stbtc token amount.
 *
 * The calculations were based on `_updateFee` (and `mintReceipt`) function from
 * Portal contract.
 * See: https://github.com/thesis/mezo-portal/blob/main/solidity/contracts/Portal.sol
 *
 * One small caveat here - in the contract the fee is calculated based on the
 * current time. Here we use time when we opened stbtcMintModal (`modalOpenedAt`
 * from redux store) and add 15 minutes to it. The reason for that is to make
 * sure the max amount won't change when user click max button and wait couple
 * of minutes before clicking mint.
 *
 * @param depositedAmount - amount of deposited tokens in wei unit with 18
 * decimals
 * @param mintedAmount - amount of minted stBTC tokens in wei unit with 18
 * decimals
 * @param mintingFeeInfo - general minting fee data from chain
 * @param depositFeeData - fee data related to specific deposit
 * @param fromTimestamp - optionally, we can specify the time from which we want
 * to calculate the max value
 * @returns bigint value of the maximum stbtc mint amount in 18 decimals
 */
export const calculateMaxStbtcMintAmount = (
  depositedAmount: string,
  mintedAmount: string,
  mintingFeeInfo: ReceiptTokenFeeInfo,
  depositFeeData: DepositFeeData,
  fromTimestamp?: number | null,
): bigint => {
  const { mintCap, lastFeeUpdateAt, annualFee, feeIntegral } = mintingFeeInfo

  const maxTotalAmountToMint = (BigInt(depositedAmount) * mintCap) / 100n

  // YEAR is in miliseconds so we divide it by 1000
  const feePerSecond = (annualFee * 10n ** 16n) / (BigInt(YEAR) / 1000n)

  const initialTime = new Date(fromTimestamp || Date.now())

  const feeCalculationTime = DateTime.fromJSDate(initialTime)
    .plus({ minutes: 15 })
    .toUnixInteger()

  const dateDifferenceInSecond = BigInt(feeCalculationTime) - lastFeeUpdateAt
  const accumulatedFee = feePerSecond * dateDifferenceInSecond
  const newFeeIntegral = feeIntegral + accumulatedFee
  const feeIntegralDifference = newFeeIntegral - depositFeeData.lastFeeIntegral

  const finalFeeOwed =
    BigInt(depositFeeData.feeOwed) +
    (feeIntegralDifference * BigInt(mintedAmount)) / 10n ** 18n

  const maxPossibleAmountToMint =
    maxTotalAmountToMint - BigInt(mintedAmount) - finalFeeOwed

  if (maxPossibleAmountToMint < 0n) {
    return 0n
  }

  return maxPossibleAmountToMint
}
